/**
 * TanStack Query hooks for reviewer per-finding verdict persistence.
 *
 * Reads from `/api/review/cohort/<cohort>/study/<uid>/finding-verdicts` and
 * writes via `/api/review/cohort/<cohort>/study/<uid>/finding-verdict`. The
 * backend stores idempotent PutItem rows (`REVIEWER_VERDICT#`) in
 * `a2z-medi-flow`; `pending` is a stored value, not an absence of row. The
 * frontend treats absent and pending identically.
 *
 * Vocabulary is external-eval per-finding agreement: agree / disagree / skip,
 * with a `reason` required on disagree.
 *
 * Auth: `/api/review/*` is Bearer-token gated (see review_endpoints.py
 * token_validator_helper), unlike the cookie-based surfacer-decision routes.
 * The query key is scoped to the caller so an admin impersonating a reviewer
 * never reads the admin's cached verdicts.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export type FindingVerdict = "agree" | "disagree" | "skip" | "pending";
export type FindingReason =
  | "incorrect_anatomy"
  | "wrong_severity"
  | "artifact"
  | "below_threshold"
  | "other";

export interface FindingVerdictEntry {
  verdict: FindingVerdict;
  reason: FindingReason | null;
  // Free text for the `other` reason; null otherwise.
  reason_detail: string | null;
}

export type FindingVerdictMap = Record<string, FindingVerdictEntry>;

interface FindingVerdictsResponse {
  verdicts: FindingVerdictMap;
}

export const reviewerFindingVerdictKeys = {
  all: ["reviewer-finding-verdicts"] as const,
  study: (reviewerKey: string, cohortId: string, studyUid: string) =>
    [...reviewerFindingVerdictKeys.all, reviewerKey, cohortId, studyUid] as const,
};

export const reviewerAgreementStatsKeys = {
  all: ["reviewer-agreement-stats"] as const,
  cohort: (reviewerKey: string, cohortId: string) =>
    [...reviewerAgreementStatsKeys.all, reviewerKey, cohortId] as const,
};

// Agreement-only cohort rollup — no sensitivity/PPV (drafter/cortex cohorts
// carry no ground-truth labels). `agreement_rate` is agreed/(agreed+disagreed).
export interface ReviewerAgreementStats {
  agreement_rate: number | null;
  cases_reviewed: number;
}

async function fetchFindingVerdicts(
  token: string,
  cohortId: string,
  studyUid: string,
): Promise<FindingVerdictMap> {
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  const response = await axios.get<FindingVerdictsResponse>(
    `${getApiBaseUrl()}/api/review/cohort/${encodedCohort}/study/${encodedUid}/finding-verdicts`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data.verdicts;
}

async function postFindingVerdict(
  token: string,
  cohortId: string,
  studyUid: string,
  finding: string,
  verdict: FindingVerdict,
  reason: FindingReason | null,
  reasonDetail: string | null,
): Promise<void> {
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  await axios.post(
    `${getApiBaseUrl()}/api/review/cohort/${encodedCohort}/study/${encodedUid}/finding-verdict`,
    { finding, verdict, reason, reason_detail: reasonDetail },
    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } },
  );
}

export function useReviewerFindingVerdicts(
  token: string | null,
  reviewerKey: string | null,
  cohortId: string | null,
  studyUid: string | null,
) {
  const queryClient = useQueryClient();
  const queryKey = reviewerFindingVerdictKeys.study(
    reviewerKey || "",
    cohortId || "",
    studyUid || "",
  );

  const query = useQuery({
    queryKey,
    queryFn: () => fetchFindingVerdicts(token!, cohortId!, studyUid!),
    enabled: !!token && !!cohortId && !!studyUid,
    staleTime: 5_000,
    gcTime: 10 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (vars: {
      finding: string;
      verdict: FindingVerdict;
      reason: FindingReason | null;
      reasonDetail: string | null;
    }) =>
      postFindingVerdict(
        token!,
        cohortId!,
        studyUid!,
        vars.finding,
        vars.verdict,
        vars.reason,
        vars.reasonDetail,
      ),
    onMutate: async ({ finding, verdict, reason, reasonDetail }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<FindingVerdictMap>(queryKey) ?? {};
      queryClient.setQueryData<FindingVerdictMap>(queryKey, {
        ...previous,
        [finding]: { verdict, reason, reason_detail: reasonDetail },
      });
      return { previous };
    },
    onError: (err, vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      // The optimistic entry snaps back above; log so a silently-reverted
      // verdict is traceable. The backend write is an idempotent PutItem on a
      // deterministic SK, so re-clicking is a safe retry.
      console.error(`[reviewer] finding-verdict save failed for ${vars.finding}`, err);
    },
    onSettled: () => {
      // Cohort agreement rollup is derived from these verdicts — refetch it
      // so "stats update as you review".
      queryClient.invalidateQueries({
        queryKey: reviewerAgreementStatsKeys.cohort(reviewerKey || "", cohortId || ""),
      });
    },
  });

  return {
    verdicts: query.data ?? {},
    isLoading: query.isLoading,
    setVerdict: (
      finding: string,
      verdict: FindingVerdict,
      reason: FindingReason | null = null,
      reasonDetail: string | null = null,
    ) => {
      mutation.mutate({ finding, verdict, reason, reasonDetail });
    },
  };
}

async function fetchAgreementStats(
  token: string,
  cohortId: string,
): Promise<ReviewerAgreementStats> {
  const response = await axios.get<ReviewerAgreementStats>(
    `${getApiBaseUrl()}/api/review/cohort/${encodeURIComponent(cohortId)}/agreement-stats`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
}

export function useReviewerAgreementStats(
  token: string | null,
  reviewerKey: string | null,
  cohortId: string | null,
) {
  return useQuery({
    queryKey: reviewerAgreementStatsKeys.cohort(reviewerKey || "", cohortId || ""),
    queryFn: () => fetchAgreementStats(token!, cohortId!),
    enabled: !!token && !!cohortId,
    staleTime: 5_000,
  });
}
